<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';
import Button from 'dashboard/components-next/button/Button.vue';
// == Unichat Extension ==
import { getSectors, getSectorTeams, addSectorAgent } from 'dashboard/unichat/api/unichat.js';

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

// == Unichat Extension: Secretaria → Setor em cascata ==
const unichatSectors = ref([]);
const unichatSelectedSectorId = ref('');
const unichatSectorTeams = ref([]);
const unichatSelectedTeamId = ref('');
const unichatLoadingTeams = ref(false);

const accountId = computed(() => store.getters.getCurrentAccountId);

onMounted(async () => {
  try {
    const { data } = await getSectors(accountId.value);
    unichatSectors.value = Array.isArray(data) ? data : [];
  } catch {
    // secretarias indisponíveis — campos ficam ocultos
  }
});

watch(unichatSelectedSectorId, async (sectorId) => {
  unichatSelectedTeamId.value = '';
  unichatSectorTeams.value = [];
  if (!sectorId) return;
  unichatLoadingTeams.value = true;
  try {
    const { data } = await getSectorTeams(accountId.value, sectorId);
    unichatSectorTeams.value = Array.isArray(data) ? data : [];
  } catch {
    // setores indisponíveis
  } finally {
    unichatLoadingTeams.value = false;
  }
});

const agentName = ref('');
const agentEmail = ref('');
const selectedRoleId = ref('agent');

const rules = {
  agentName: { required },
  agentEmail: { required, email },
  selectedRoleId: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  agentEmail,
  selectedRoleId,
});

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const addAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = {
      name: agentName.value,
      email: agentEmail.value,
    };

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else {
      payload.role = selectedRole.value.name;
    }

    await store.dispatch('agents/create', payload);

    // == Unichat Extension: vincula à Secretaria e/ou Setor ==
    // O store não retorna o agente criado — buscamos pelo email no store atualizado
    if (unichatSelectedSectorId.value || unichatSelectedTeamId.value) {
      const createdAgent = store.getters['agents/getAgents']
        .find(a => a.email === agentEmail.value);

      if (createdAgent?.id) {
        if (unichatSelectedSectorId.value) {
          try {
            await addSectorAgent(accountId.value, unichatSelectedSectorId.value, createdAgent.id);
          } catch { /* vínculo com secretaria falhou silenciosamente */ }
        }
        if (unichatSelectedTeamId.value) {
          try {
            await store.dispatch('teamMembers/create', {
              teamId: unichatSelectedTeamId.value,
              agentsList: [createdAgent.id],
            });
          } catch { /* vínculo com setor falhou silenciosamente */ }
        }
      }
    }

    useAlert(t('AGENT_MGMT.ADD.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    const {
      response: {
        data: {
          error: errorResponse = '',
          attributes: attributes = [],
          message: attrError = '',
        } = {},
      } = {},
    } = error;

    let errorMessage = '';
    if (error?.response?.status === 422 && !attributes.includes('base')) {
      errorMessage = t('AGENT_MGMT.ADD.API.EXIST_MESSAGE');
    } else {
      errorMessage = t('AGENT_MGMT.ADD.API.ERROR_MESSAGE');
    }
    useAlert(errorResponse || attrError || errorMessage);
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('AGENT_MGMT.ADD.TITLE')"
      :header-content="$t('AGENT_MGMT.ADD.DESC')"
    />
    <form class="flex flex-col items-start w-full" @submit.prevent="addAgent">
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.LABEL') }}
          <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentEmail.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.EMAIL.LABEL') }}
          <input
            v-model="agentEmail"
            type="email"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.EMAIL.PLACEHOLDER')"
            @input="v$.agentEmail.$touch"
          />
        </label>
      </div>

      <!-- == Unichat Extension: Secretaria → Setor == -->
      <div v-if="unichatSectors.length" class="w-full mt-1">
        <label>
          Secretaria <span class="text-xs text-gray-400">(opcional)</span>
          <select v-model="unichatSelectedSectorId">
            <option value="">Nenhuma</option>
            <option
              v-for="sector in unichatSectors"
              :key="sector.id"
              :value="sector.id"
            >
              {{ sector.name }}
            </option>
          </select>
        </label>
      </div>

      <div
        v-if="unichatSelectedSectorId"
        class="w-full mt-1"
      >
        <label>
          Setor <span class="text-xs text-gray-400">(opcional)</span>
          <select
            v-model="unichatSelectedTeamId"
            :disabled="unichatLoadingTeams"
          >
            <option value="">
              {{ unichatLoadingTeams ? 'Carregando...' : 'Nenhum (apenas na Secretaria)' }}
            </option>
            <option
              v-for="team in unichatSectorTeams"
              :key="team.id"
              :value="team.id"
            >
              {{ team.name }}
            </option>
          </select>
          <span v-if="!unichatLoadingTeams && unichatSelectedSectorId && !unichatSectorTeams.length" class="message">
            Nenhum setor vinculado a esta secretaria ainda.
          </span>
        </label>
      </div>
      <!-- == /Unichat Extension == -->

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('AGENT_MGMT.ADD.CANCEL_BUTTON_TEXT')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="$t('AGENT_MGMT.ADD.FORM.SUBMIT')"
          :disabled="v$.$invalid || uiFlags.isCreating"
          :is-loading="uiFlags.isCreating"
        />
      </div>
    </form>
  </div>
</template>
